import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { getCart, removeFromCart } from "../../requests/cart/cart";
import { createOrder } from "../../requests/catalog/catalog";
import { getApiErrorMessage } from "../../requests/apiClient";
import styles from "./BucketPage.module.css";
import type { CatalogItem } from "../../types/catalog";
import { useCartStore } from "../../store/useCartStore";
import { useNavMenuStore } from "../../store/useNavMenuStore";
import { useNotificationStore } from "../../store/NotificationStore";
import { BackButton } from "../../components/BackButton/BackButton";
import Loader from "../../components/Loader/Loader";
import buyPicture from "../../assets/buyPicture.webp";
import maxIcon from "../../assets/max.png";
import phoneIcon from "../../assets/phone.png";
import { ReactComponent as CloseIcon } from "../../assets/closeIcon.svg";
import { ReactComponent as HeartNavIcon } from "../../assets/heart.svg";
import { ReactComponent as CatalogNavIcon } from "../../assets/catalog.svg";
import { ReactComponent as BucketNavIcon } from "../../assets/bucket.svg";
import { ReactComponent as SupportNavIcon } from "../../assets/support.svg";
import { ReactComponent as TelegramContactIcon } from "../../assets/telegram.svg";

interface CartItem {
  id: number;
  product: CatalogItem;
  size: string;
  quantity: number;
  price: number;
  discount: number;
  cart_item: {
    size: string;
    id: number;
  };
}

type ContactChannel = "max" | "telegram" | "email";

const CONTACT_CHANNEL_META: Record<
  ContactChannel,
  { label: string; buttonLabel: string }
> = {
  max: {
    label: "Max",
    buttonLabel: "Написать в Max",
  },
  telegram: {
    label: "Telegram",
    buttonLabel: "Написать в Telegram",
  },
  email: {
    label: "Телефон",
    buttonLabel: "Позвонить на номер",
  },
};

const BucketPage = () => {
  const tg = window.Telegram?.WebApp;
  const navigate = useNavigate();
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth > 500);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalPrice, setTotalPrice] = useState(0);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [contactChannel, setContactChannel] =
    useState<ContactChannel>("telegram");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const { updateCartCount } = useCartStore();
  const { setVisibility: setNavMenuVisibility } = useNavMenuStore();
  const setNotification = useNotificationStore(
    (state) => state.setNotification,
  );
  useEffect(() => {
    const onResize = () => {
      setIsDesktop(window.innerWidth > 500);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    if (isDesktop) {
      setNavMenuVisibility(false);
      return;
    }
    setNavMenuVisibility(true);
  }, [isDesktop, setNavMenuVisibility]);

  useEffect(() => {
    return () => {
      setNavMenuVisibility(true);
    };
  }, [setNavMenuVisibility]);

  useEffect(() => {
    const fetchCart = async () => {
      const startTime = Date.now();
      const minLoadingTime = 2000;

      try {
        setIsLoading(true);
        const response = await getCart();
        const items = response.items || [];
        setCartItems(items);
        setTotalPrice(response.final_price);
      } catch (error) {
        console.error("Ошибка при получении корзины:", error);
      } finally {
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

        if (remainingTime > 0) {
          setTimeout(() => {
            setIsLoading(false);
          }, remainingTime);
        } else {
          setIsLoading(false);
        }
      }
    };
    fetchCart();
  }, []);

  const formatPrice = (price: number): string => {
    return Math.round(price)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  const calculateDiscountedPrice = (item: CatalogItem): number | null => {
    if (!item.discount || !item.discount.value) {
      return null;
    }

    const { type, value } = item.discount;

    if (type === "percentage") {
      const discountAmount = (item.price.amount * value) / 100;
      return item.price.amount - discountAmount;
    }

    if (type === "fixed") {
      return Math.max(0, item.price.amount - value);
    }

    return null;
  };

  const handleRemoveClick = async (cartItem: CartItem) => {
    try {
      await removeFromCart(cartItem.cart_item.id);
      setCartItems((prev) =>
        prev.filter((item) => item.cart_item.id !== cartItem.cart_item.id),
      );

      const discountedPrice = calculateDiscountedPrice(cartItem.product);
      const itemPrice =
        discountedPrice !== null
          ? discountedPrice
          : cartItem.product.price.amount;

      setTotalPrice((prev) => prev - itemPrice);
      await updateCartCount();
    } catch (error) {
      console.error("Ошибка при удалении из корзины:", error);
    }
  };

  const handleCreateOrder = async () => {
    const normalizedPhone = phone.trim();
    if (!normalizedPhone) {
      setNotification({
        type: "Error",
        message: "Введите номер телефона для оформления заказа",
      });
      return;
    }

    if (!normalizedPhone.startsWith("+7")) {
      setNotification({
        type: "Error",
        message: "Номер телефона должен начинаться с +7",
      });
      return;
    }

    if (normalizedPhone.length !== 12 || !/^\+7\d{10}$/.test(normalizedPhone)) {
      setNotification({
        type: "Error",
        message: "Неправильно введен номер",
      });
      return;
    }

    setIsSubmittingOrder(true);
    console.log(contactChannel);
    try {
      await createOrder({
        phone: normalizedPhone,
        notes: notes.trim() || undefined,
        contact: contactChannel,
      });

      const response = await getCart();
      const items = response.items || [];
      setCartItems(items);
      setTotalPrice(response.final_price);
      await updateCartCount();

      setIsCheckoutModalOpen(false);
      setIsOrderModalOpen(true);
      setPhone("");
      setNotes("");
      setNotification({
        type: "Success",
        message: "Заказ успешно создан",
      });
    } catch (error) {
      setNotification({
        type: "Error",
        message: getApiErrorMessage(error),
      });
      console.error("Ошибка при создании заказа:", error);
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  const handleManagerContactClick = () => {
    if (tg?.openTelegramLink) {
      tg.openTelegramLink("https://t.me/menpoint1");
      return;
    }
    window.open("https://t.me/menpoint1", "_blank", "noopener,noreferrer");
  };

  const openExternalLink = (url: string) => {
    if (tg?.openLink) {
      tg.openLink(url);
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleWriteToManager = () => {
    setIsOrderModalOpen(false);
  };

  const totalItemsCount = cartItems.reduce((total, item) => {
    return total + (item.quantity || 1);
  }, 0);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div
      className={`${styles.container} ${!isDesktop ? styles.mobileTopOffset : ""} ${
        !isDesktop && cartItems.length > 0 ? styles.mobileCheckoutOffset : ""
      }`}
    >
      {!isDesktop && <BackButton />}
      {isDesktop && (
        <div className={styles.desktopToolbar}>
          <div className={styles.desktopTopNav}>
            <button
              className={`${styles.desktopTopNavButton} ${styles.desktopTopNavIconOnlyButton}`}
              onClick={() => navigate("/catalog")}
              aria-label="Catalog"
            >
              <CatalogNavIcon className={styles.desktopTopNavIcon} />
              <span>Избранное</span>
            </button>
            <button
              className={`${styles.desktopTopNavButton} ${styles.desktopTopNavIconOnlyButton}`}
              onClick={() => navigate("/saved")}
              aria-label="Saved"
            >
              <HeartNavIcon className={styles.desktopTopNavIcon} />
              <span>Каталог</span>
            </button>
            <button
              className={`${styles.desktopTopNavButton} ${styles.desktopTopNavIconOnlyButton}`}
              onClick={handleManagerContactClick}
              aria-label="Manager"
            >
              <SupportNavIcon className={styles.desktopTopNavIcon} />
              <span>Корзина ({totalItemsCount})</span>
            </button>
            <button
              className={`${styles.desktopTopNavButton} ${styles.desktopTopNavButtonActive} ${styles.desktopTopNavIconOnlyButton}`}
              onClick={() => navigate("/bucket")}
              aria-label="Cart"
            >
              <BucketNavIcon className={styles.desktopTopNavIcon} />
              {totalItemsCount > 0 && (
                <span className={styles.desktopTopNavBadge}>
                  {totalItemsCount}
                </span>
              )}
              <span>Менеджер</span>
            </button>
          </div>
        </div>
      )}

      <div className={styles.bucketPageLayout}>
        <div className={styles.bucketPageContent}>
          <div className={styles.bucketPageTitle}>Ваша корзина</div>
          {cartItems.length > 0 ? (
            <div className={styles.bucketPageItems}>
              <AnimatePresence mode="popLayout">
                {cartItems.map((cartItem, index) => {
                  const discountedPrice = calculateDiscountedPrice(
                    cartItem.product,
                  );
                  return (
                    <motion.div
                      key={cartItem.cart_item.id}
                      className={styles.bucketPageItem}
                      initial={{ opacity: 0, scale: 0.9, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{
                        opacity: 0,
                        scale: 0.8,
                        x: -100,
                        transition: {
                          duration: 0.4,
                          ease: [0.25, 0.46, 0.45, 0.94],
                        },
                      }}
                      transition={{
                        duration: 0.3,
                        delay: index * 0.05,
                        ease: [0.25, 0.46, 0.45, 0.94],
                      }}
                      layout
                    >
                      <div className={styles.bucketPageItemImage}>
                        <img
                          src={cartItem.product.view_details.images[0]}
                          alt={cartItem.product.view_details.title}
                        />
                      </div>
                      <div className={styles.bucketPageItemInfo}>
                        <div className={styles.bucketPageItemTitle}>
                          {cartItem.product.view_details.title}
                        </div>
                        <div className={styles.bucketPageItemPrice}>
                          {discountedPrice !== null ? (
                            <>
                              <span
                                className={styles.bucketPageItemDiscountPrice}
                              >
                                {formatPrice(discountedPrice)} ₽
                              </span>
                              <span
                                className={styles.bucketPageItemOriginalPrice}
                              >
                                {formatPrice(cartItem.product.price.amount)} ₽
                              </span>
                            </>
                          ) : (
                            <span
                              className={styles.bucketPageItemDiscountPrice}
                            >
                              {formatPrice(cartItem.product.price.amount)} ₽
                            </span>
                          )}
                        </div>
                        <div className={styles.bucketPageItemSize}>
                          Размер: {cartItem.cart_item.size}
                        </div>
                      </div>
                      <div
                        className={styles.bucketPageItemClose}
                        onClick={() => handleRemoveClick(cartItem)}
                      >
                        <motion.div
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 0.3 }}
                        >
                          <CloseIcon
                            style={{ width: "20px", height: "20px" }}
                          />
                        </motion.div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          ) : (
            <div className={styles.noItems}>
              Ваша корзина пуста, добавьте товары из каталога.
            </div>
          )}
        </div>

        {cartItems.length > 0 && isDesktop && (
          <aside className={styles.bucketSummaryCard}>
            <div className={styles.bucketSummaryTitle}>Итого</div>
            <div className={styles.bucketSummaryRow}>
              <span>Товаров</span>
              <span>{totalItemsCount}</span>
            </div>
            <div className={styles.bucketSummaryRow}>
              <span>Сумма</span>
              <strong>{formatPrice(totalPrice)} ₽</strong>
            </div>
            <button
              className={styles.bucketPageButton}
              onClick={() => setIsCheckoutModalOpen(true)}
            >
              Оформить заказ
            </button>
            <button
              className={styles.bucketSummaryManagerButton}
              onClick={handleManagerContactClick}
            >
              Написать менеджеру
            </button>
          </aside>
        )}
      </div>

      {cartItems.length > 0 && !isDesktop && (
        <div className={styles.bucketPageButtonContainer}>
          <button
            className={styles.bucketPageButton}
            onClick={() => setIsCheckoutModalOpen(true)}
          >
            Оформить заказ {formatPrice(totalPrice)} ₽
          </button>
        </div>
      )}

      <AnimatePresence>
        {isCheckoutModalOpen && (
          <motion.div
            className={styles.orderModalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsCheckoutModalOpen(false)}
          >
            <motion.div
              className={styles.orderModalContent}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.orderModalTitle}>Оформление заказа</div>
              <div className={styles.orderModalText}>
                Укажите телефон, чтобы менеджер мог подтвердить заказ.
              </div>
              <div className={styles.orderFormGroup}>
                <label
                  className={styles.orderFormLabel}
                  htmlFor="checkout-phone"
                >
                  Телефон *
                </label>
                <input
                  id="checkout-phone"
                  className={styles.orderFormInput}
                  type="tel"
                  placeholder="+79990000000"
                  maxLength={12}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <div className={styles.orderFormGroup}>
                <label
                  className={styles.orderFormLabel}
                  htmlFor="checkout-notes"
                >
                  Комментарий
                </label>
                <textarea
                  id="checkout-notes"
                  className={styles.orderFormTextarea}
                  placeholder="Например, позвонить перед доставкой"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
              <div className={styles.orderFormGroup}>
                <label className={styles.orderFormLabel}>
                  Куда написать после заказа
                </label>
                <div className={styles.orderContactChannels}>
                  <button
                    type="button"
                    className={`${styles.orderContactChannelButton} ${
                      contactChannel === "max"
                        ? styles.orderContactChannelButtonActive
                        : ""
                    }`}
                    onClick={() => setContactChannel("max")}
                  >
                    <span
                      className={`${styles.orderContactChannelIcon} ${styles.orderContactChannelIconMax}`}
                    >
                      <img src={maxIcon} alt="Max" />
                    </span>
                    <span>Max</span>
                  </button>
                  <button
                    type="button"
                    className={`${styles.orderContactChannelButton} ${
                      contactChannel === "telegram"
                        ? styles.orderContactChannelButtonActive
                        : ""
                    }`}
                    onClick={() => setContactChannel("telegram")}
                  >
                    <span
                      className={`${styles.orderContactChannelIcon} ${styles.orderContactChannelIconTelegram}`}
                    >
                      <TelegramContactIcon />
                    </span>
                    <span>Telegram</span>
                  </button>
                  <button
                    type="button"
                    className={`${styles.orderContactChannelButton} ${
                      contactChannel === "email"
                        ? styles.orderContactChannelButtonActive
                        : ""
                    }`}
                    onClick={() => setContactChannel("email")}
                  >
                    <span
                      className={`${styles.orderContactChannelIcon} ${styles.orderContactChannelIconEmail}`}
                    >
                      <img src={phoneIcon} alt="Phone" />
                    </span>
                    <span>Телефон</span>
                  </button>
                </div>
              </div>
              <button
                className={styles.orderModalPrimaryButton}
                onClick={handleCreateOrder}
                disabled={isSubmittingOrder}
              >
                {isSubmittingOrder ? "Оформляем..." : "Подтвердить заказ"}
              </button>
              <button
                className={styles.orderModalSecondaryButton}
                onClick={() => setIsCheckoutModalOpen(false)}
              >
                Отмена
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOrderModalOpen && (
          <motion.div
            className={styles.orderModalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOrderModalOpen(false)}
          >
            <motion.div
              className={styles.orderModalContent}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.orderModalImageWrapper}>
                <img
                  src={buyPicture}
                  alt="Оформление заказа"
                  className={styles.orderModalImage}
                />
              </div>
              <div className={styles.orderModalTitle}>Спасибо за заказ!</div>
              <div className={styles.orderModalText}>
                Мы свяжемся с вами в ближайшее время для подтверждения заказа.
              </div>
              <button
                className={styles.orderModalPrimaryButton}
                onClick={handleWriteToManager}
              >
                Закрыть
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BucketPage;
